// Contingency + alerts (setter spec §10.8, §10.9; UAT-46/47): AI kill switch, controlled resume, operational incidents,
// Slack incoming webhook and alert settings. Schemas in api-contingency.ts. Shared by every module (module: null).
import { z } from 'zod';
import { OkSchema } from './api.js';
import * as C from './api-contingency.js';
import { IdParam, items, route, W, Ws } from './route-def.js';

const cap = 'platform_integration_admin' as const;
const Stop = W({ stopId: IdParam });
const PlatformStop = z.object({ stopId: IdParam });

export const contingencyRoutes = {
  getContingency: route({
    method: 'GET', path: '/workspaces/:workspaceId/contingency', summary: 'Estado de contingencia: paros activos, historial e incidentes', tag: 'contingency',
    minRole: 'manager', module: null, params: Ws, response: C.ContingencyOverviewSchema,
  }),
  activateContingency: route({
    method: 'POST', path: '/workspaces/:workspaceId/contingency/stops', summary: 'Activar el interruptor de emergencia de la IA (espacio, proyecto o canal)', tag: 'contingency',
    minRole: 'manager', module: null, params: Ws, body: C.ActivateContingencySchema, response: C.ContingencyStopSchema, status: 201,
  }),
  previewContingencyResume: route({
    method: 'GET', path: '/workspaces/:workspaceId/contingency/stops/:stopId/resume-preview', summary: 'Vista previa de lo pendiente antes de reanudar', tag: 'contingency',
    minRole: 'admin', module: null, params: Stop, response: C.ResumePreviewSchema,
  }),
  resumeContingency: route({
    method: 'POST', path: '/workspaces/:workspaceId/contingency/stops/:stopId/resume', summary: 'Reanudar con conciliación (decisión auditada)', tag: 'contingency',
    minRole: 'admin', module: null, params: Stop, body: C.ResumeContingencySchema, response: C.ResumeResultSchema,
  }),
  listOpsIncidents: route({
    method: 'GET', path: '/workspaces/:workspaceId/ops-incidents', summary: 'Incidentes operativos y alertas enviadas', tag: 'contingency',
    minRole: 'manager', module: null, params: Ws, query: C.OpsIncidentQuerySchema, response: items(C.OpsIncidentSchema),
  }),
  resolveOpsIncident: route({
    method: 'POST', path: '/workspaces/:workspaceId/ops-incidents/:incidentId/resolve', summary: 'Marcar un incidente como resuelto (envía aviso de recuperación)', tag: 'contingency',
    minRole: 'admin', module: null, params: W({ incidentId: IdParam }), body: C.ResolveOpsIncidentSchema, response: C.OpsIncidentSchema,
  }),
  getContingencySettings: route({
    method: 'GET', path: '/workspaces/:workspaceId/contingency/settings', summary: 'Responsable designado y umbrales de alerta', tag: 'contingency',
    minRole: 'manager', module: null, params: Ws, response: C.ContingencySettingsSchema,
  }),
  putContingencySettings: route({
    method: 'PUT', path: '/workspaces/:workspaceId/contingency/settings', summary: 'Guardar responsable designado y umbrales de alerta', tag: 'contingency',
    minRole: 'admin', module: null, params: Ws, body: C.ContingencySettingsSchema, response: C.ContingencySettingsSchema,
  }),
  getSlackAlertChannel: route({
    method: 'GET', path: '/workspaces/:workspaceId/alert-channels/slack', summary: 'Conexión de alertas por Slack del espacio', tag: 'contingency',
    minRole: 'admin', module: null, params: Ws, response: C.SlackAlertChannelResponseSchema,
  }),
  connectSlackAlertChannel: route({
    method: 'PUT', path: '/workspaces/:workspaceId/alert-channels/slack', summary: 'Conectar o reemplazar el Incoming Webhook de Slack (secreto cifrado)', tag: 'contingency',
    minRole: 'admin', module: null, params: Ws, body: C.ConnectSlackAlertSchema, response: C.SlackAlertChannelResponseSchema,
  }),
  testSlackAlertChannel: route({
    method: 'POST', path: '/workspaces/:workspaceId/alert-channels/slack/test', summary: 'Enviar un mensaje de prueba a Slack', tag: 'contingency',
    minRole: 'admin', module: null, params: Ws, response: C.SlackTestResultSchema,
  }),
  deleteSlackAlertChannel: route({
    method: 'DELETE', path: '/workspaces/:workspaceId/alert-channels/slack', summary: 'Desconectar Slack', tag: 'contingency',
    minRole: 'admin', module: null, params: Ws, response: OkSchema,
  }),

  // ─────────── Platform (Estateflow) ───────────
  getPlatformContingency: route({ method: 'GET', path: '/platform/contingency', summary: 'Contingencia global de la plataforma', tag: 'platform', capability: cap, response: C.ContingencyOverviewSchema }),
  activateGlobalContingency: route({
    method: 'POST', path: '/platform/contingency/stops', summary: 'Detener la IA en toda la plataforma', tag: 'platform',
    capability: cap, body: C.ActivateGlobalContingencySchema, response: C.ContingencyStopSchema, status: 201,
  }),
  previewGlobalContingencyResume: route({
    method: 'GET', path: '/platform/contingency/stops/:stopId/resume-preview', summary: 'Vista previa global antes de reanudar', tag: 'platform',
    capability: cap, params: PlatformStop, response: C.ResumePreviewSchema,
  }),
  resumeGlobalContingency: route({
    method: 'POST', path: '/platform/contingency/stops/:stopId/resume', summary: 'Reanudar la IA en la plataforma con conciliación', tag: 'platform',
    capability: cap, params: PlatformStop, body: C.ResumeContingencySchema, response: C.ResumeResultSchema,
  }),
  resolvePlatformOpsIncident: route({
    method: 'POST', path: '/platform/ops-incidents/:incidentId/resolve', summary: 'Resolver un incidente de la plataforma', tag: 'platform',
    capability: cap, params: z.object({ incidentId: IdParam }), body: C.ResolveOpsIncidentSchema, response: C.OpsIncidentSchema,
  }),
  getPlatformSlackAlertChannel: route({ method: 'GET', path: '/platform/alert-channels/slack', summary: 'Slack interno de Estateflow', tag: 'platform', capability: cap, response: C.SlackAlertChannelResponseSchema }),
  connectPlatformSlackAlertChannel: route({
    method: 'PUT', path: '/platform/alert-channels/slack', summary: 'Conectar el Slack interno de Estateflow', tag: 'platform',
    capability: cap, body: C.ConnectSlackAlertSchema, response: C.SlackAlertChannelResponseSchema,
  }),
  testPlatformSlackAlertChannel: route({ method: 'POST', path: '/platform/alert-channels/slack/test', summary: 'Prueba del Slack interno', tag: 'platform', capability: cap, response: C.SlackTestResultSchema }),
};
